import { playAmbient, stopAmbient } from '../audio/ambient';
import * as fx from '../audio/fx';
import { MINUTE, PHASE_LABEL, PRESETS } from '../core/constants';
import { recordSession } from '../core/history';
import { creditActive } from '../core/tasks';
import * as T from '../core/timer';
import type { PresetKey, Settings } from '../core/types';
import { dayKey } from '../core/util';
import { track, trackFocus, trackTask } from '../platform/analytics';
import { notify } from '../platform/notify';
import { setWakeLock } from '../platform/wakelock';
import { announce } from '../ui/announcer';
import { toast } from '../ui/toast';
import { app, commit, emit, persist, syncFromStorage } from './state';

const now = () => Date.now();
const run = () => app.data.run;

/** Hooks the view layer registers (kept as callbacks to avoid import cycles). */
export const viewHooks = { wash: () => {}, phaseChanged: () => {} };

/* ---------- side effects that follow the run state ---------- */

export function syncRunEffects() {
  const r = run();
  const s = app.data.settings;
  if (r.running && r.phase === 'focus' && s.ambient !== 'off') playAmbient(s.ambient);
  else stopAmbient();
  setWakeLock(s.wakeLock && r.running);
}

function setRun(next: typeof app.data.run, opts: { phaseChanged?: boolean } = {}) {
  app.data.run = next;
  persist();
  emit('run');
  if (opts.phaseChanged) viewHooks.phaseChanged();
  syncRunEffects();
}

/* ---------- actions ---------- */

export function start() {
  fx.ensureAudio();
  const r = run();
  if (r.running) return;
  setRun(T.start(r, now()));
  announce(`${PHASE_LABEL[r.phase].toLowerCase()} started`);
  if (r.phase === 'focus' && r.remaining >= r.total) {
    trackFocus('start');
    const amb = app.data.settings.ambient;
    if (amb !== 'off') track(`ambient_${amb}`);
  }
}

export function pause() {
  const r = run();
  if (!r.running) return;
  setRun(T.pause(r, now()));
  announce('Paused');
}

export const toggle = () => (run().running ? pause() : start());

export function reset() {
  setRun(T.reset(run(), app.data.settings));
  announce(`${PHASE_LABEL[run().phase].toLowerCase()} reset`);
}

export function skip() {
  fx.tap();
  const r = T.complete(run(), app.data.settings, { endedAt: now(), natural: false, now: now() });
  setRun(r.run, { phaseChanged: true });
  viewHooks.wash();
  announce(`Skipped to ${PHASE_LABEL[run().phase].toLowerCase()}`);
}

export function logInterruption() {
  const r = run();
  if (r.phase !== 'focus' || !T.inProgress(r)) return false;
  setRun({ ...r, interruptions: r.interruptions + 1 });
  fx.tap();
  announce(`Interruption logged, ${r.interruptions + 1} this session`);
  return true;
}

/** Updates a duration setting; an untouched phase picks it up right away. */
export function setSetting<K extends keyof Settings>(key: K, value: Settings[K]) {
  app.data.settings = { ...app.data.settings, [key]: value };
  app.data.run = T.applySettings(run(), app.data.settings);
  persist();
  emit('settings', 'run');
  syncRunEffects();
}

export function setFocusMinutes(m: number) {
  if (m !== app.data.settings.focus) setSetting('focus', m);
}

/** Returns false when the switch needs confirmation because a session is in progress. */
export function applyPreset(key: PresetKey, force = false): boolean {
  if (!force && T.inProgress(run())) return false;
  const p = PRESETS[key];
  app.data.settings = { ...app.data.settings, focus: p.focus, short: p.short, long: p.long };
  app.data.run = T.idleRun(app.data.settings, 'focus', 1);
  persist();
  emit('settings', 'run');
  viewHooks.phaseChanged();
  syncRunEffects();
  return true;
}

/* ---------- completion handling ---------- */

function handleCompletions(completions: T.PhaseCompletion[], live: boolean) {
  const d = app.data;
  let lastFocus: T.PhaseCompletion | null = null;
  for (const c of completions) {
    if (c.phase !== 'focus') {
      trackFocus('break');
      continue;
    }
    trackFocus('done', c.minutes, c.interruptions);
    lastFocus = c;
    const active = d.tasks.find((t) => t.id === d.activeTaskId && !t.done);
    recordSession(d.history, {
      ts: c.endedAt,
      min: c.minutes,
      task: active?.text ?? null,
      interruptions: c.interruptions,
    });
    d.totals.sessions++;
    d.totals.minutes += c.minutes;
    const credit = creditActive(d.tasks, d.activeTaskId);
    d.activeTaskId = credit.activeId;
    if (credit.finished) trackTask('done');
    if (credit.finished && live) {
      const next = d.tasks.find((t) => t.id === credit.activeId);
      toast(
        next
          ? `${credit.finished.text} done — next: ${next.text}`
          : `${credit.finished.text} done — all tasks complete`,
        'var(--sage)',
      );
    }
  }
  commit(['stats', 'tasks']);

  const last = completions[completions.length - 1];
  if (!last) return;
  if (!live) {
    const n = completions.filter((c) => c.phase === 'focus').length;
    toast(
      n
        ? `While you were away: ${n} focus session${n > 1 ? 's' : ''} completed`
        : 'Timer finished while you were away',
    );
    return;
  }
  fx.chime(d.settings.sound);
  if (last.phase === 'focus' && lastFocus) {
    const next = run();
    const bMin = Math.round(next.total / MINUTE);
    const color = next.phase === 'long' ? 'var(--pine)' : 'var(--sage)';
    toast(`Focus complete — ${bMin} min break`, color);
    void notify(
      'Focus complete',
      `${lastFocus.minutes} minutes of deep work. Time for a ${bMin} minute break.`,
    );
    announce(`Focus complete. ${PHASE_LABEL[next.phase].toLowerCase()}, ${bMin} minutes.`);
  } else {
    toast("Break's over — back to focus", 'var(--tomato)');
    void notify('Break over', 'Back to focus.');
    announce('Break over. Back to focus.');
  }
}

/* ---------- tick ---------- */

let today = dayKey(Date.now());

/** Called every animation frame and by the background heartbeat. Idempotent. */
export function tick() {
  const t = now();
  const k = dayKey(t);
  if (k !== today) {
    today = k;
    emit('stats'); // midnight rollover while the app stays open
  }
  const r = run();
  if (r.running && r.endAt > t + r.total) {
    // The system clock moved backwards: never show more than a full phase.
    app.data.run = T.capEndAt(r, t);
    persist();
    emit('run');
    return;
  }
  if (!r.running || r.endAt > t) return;
  // Another tab may already have completed this phase — adopt its state instead of double-logging.
  if (syncFromStorage()) {
    emit('run', 'settings', 'tasks', 'stats', 'side');
    viewHooks.phaseChanged();
    if (!run().running || run().endAt > t) return syncRunEffects();
  }
  const { run: next, completions } = T.advance(run(), app.data.settings, t);
  app.data.run = next;
  // A single phase that just ended is "live" (chime, toast); anything older is a catch-up summary.
  const live = completions.length === 1 && t - completions[0]!.endedAt < 10_000;
  handleCompletions(completions, live);
  persist();
  emit('run');
  viewHooks.phaseChanged();
  viewHooks.wash();
  syncRunEffects();
}
